import {
    Classes,
    Intent,
    Menu,
    MenuItem,
    Position,
    Toaster
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Banner } from "components/common/Banner";
import {
    segmentTrackDownloadSensitivitiesTemplate,
    segmentTrackSetSensitivities,
    segmentTrackUploadSensitivities
} from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useAllAvailableProjects } from "in_queue/contexts/ProjectDataContext";
import { ClusterProjectRow } from "in_queue/types/clusterProjectRowType";
import {
    getStudyCycleLabel,
    getStudyGroupLabel
} from "in_queue/types/clusterType";
import {
    ProjectType,
    QueueProject,
    SpeculativeProject
} from "in_queue/types/projectType";
import {
    MisoPercentageReduction,
    ProjectSizeAdjustmentType,
    ScenarioAssumptionsCSV,
    ScenarioAssumptionsCSVFields
} from "in_queue/types/scenarioType";
import { keyBy } from "lodash";
import { ParseResult } from "papaparse";
import { RegionType } from "types/regionType";
import { downloadJSONToCSV } from "utils/csv";
import {
    parseSensitivitiesCsvFile,
    readSensitivitiesCsv
} from "./sensitivitiesCsv";
import css from "./UploadSensitivitiesMenu.module.scss";
import { useSensitivitiesCsvRules } from "./useSensitivitiesCsvRules";

interface UploadSensitivitiesProps {
    projectSizeAssumptions: Record<string, MisoPercentageReduction>;
    setProjectSizeAssumptions: (
        newProjectSizeAssumptions: Record<string, MisoPercentageReduction>
    ) => void;
    includedSpeculativeProjectIds: ReadonlyArray<string>;
    setIncludedSpeculativeProjectIds: (newIds: ReadonlyArray<string>) => void;
    baseScenarioRows: ReadonlyArray<ClusterProjectRow>;
}
export const UploadSensitivitiesMenu: React.FC<UploadSensitivitiesProps> = ({
    projectSizeAssumptions,
    setProjectSizeAssumptions,
    includedSpeculativeProjectIds,
    setIncludedSpeculativeProjectIds,
    baseScenarioRows
}) => {
    const cluster = useCluster();
    const csvRules = useSensitivitiesCsvRules();
    const availableProjects = useAllAvailableProjects();

    if (csvRules === undefined || availableProjects === "loading") {
        return null;
    }

    const { onlyExclusionSupported, validProjectIds, speculativeProjectIds } =
        csvRules;
    const projectById = new Map(
        availableProjects.map((project) => [project.projectId, project])
    );

    const includeWithdrawnHint = onlyExclusionSupported
        ? "In this stage a CSV cannot re-add them."
        : "To include one in the scenario, specify a reduction other than 100%.";

    const isMiso = cluster.region === RegionType.MISO;

    const statsByProjectId = keyBy(baseScenarioRows, "projectId");

    const buildTemplateRow = ({
        projectId,
        project,
        erisPercentageReduction,
        nrisPercentageReduction
    }: {
        projectId: string;
        project?: QueueProject | SpeculativeProject;
        erisPercentageReduction?: number | "";
        nrisPercentageReduction?: number | "";
    }): ScenarioAssumptionsCSV => {
        // Withdrawn status is surfaced in its own `status` column, so the study
        // cycle is left as the plain cycle label (no "(Withdrawn)" suffix).
        const statusLabel =
            project !== undefined && project.type === ProjectType.QUEUE_PROJECT
                ? project.status
                : "";
        const studyCycleLabel =
            project === undefined
                ? ""
                : getStudyCycleLabel(
                      project.cluster.region,
                      project.cluster.studyCycle
                  );

        const row: ScenarioAssumptionsCSV = {
            [ScenarioAssumptionsCSVFields.project_id]: projectId,
            [ScenarioAssumptionsCSVFields.eris_percentage_reduction]:
                erisPercentageReduction ?? "",
            [ScenarioAssumptionsCSVFields.nris_percentage_reduction]:
                nrisPercentageReduction ?? "",
            [ScenarioAssumptionsCSVFields.study_cycle]: studyCycleLabel,
            ...(isMiso
                ? {
                      [ScenarioAssumptionsCSVFields.study_group]:
                          project === undefined
                              ? ""
                              : getStudyGroupLabel(
                                    project.cluster.region,
                                    project.cluster.studyGroup
                                ) ?? ""
                  }
                : {}),
            [ScenarioAssumptionsCSVFields.status]: statusLabel,
            [ScenarioAssumptionsCSVFields.is_withdrawn]:
                project !== undefined &&
                project.type === ProjectType.QUEUE_PROJECT
                    ? project.isWithdrawn
                    : "",
            [ScenarioAssumptionsCSVFields.fuel_type]: project?.fuelType ?? "",
            [ScenarioAssumptionsCSVFields.energy_mw]:
                project?.size.erisMw ?? "",
            [ScenarioAssumptionsCSVFields.capacity_mw]:
                project?.size.nrisMw ?? "",
            [ScenarioAssumptionsCSVFields.base_cost_per_mw]:
                statsByProjectId[projectId]?.costPerMw ?? "",
            [ScenarioAssumptionsCSVFields.base_total_cost]:
                statsByProjectId[projectId]?.sumAllocatedCost ?? "",
            [ScenarioAssumptionsCSVFields.base_upgrades]:
                statsByProjectId[projectId]?.numAssignedUpgrades ?? "",
            [ScenarioAssumptionsCSVFields.base_rank]:
                statsByProjectId[projectId]?.rankStats?.rank ?? "",
            [ScenarioAssumptionsCSVFields.base_percentile]:
                statsByProjectId[projectId]?.rankStats?.percentile ?? ""
        };

        if (onlyExclusionSupported) {
            /* eslint-disable @typescript-eslint/no-unused-vars */
            const {
                [ScenarioAssumptionsCSVFields.eris_percentage_reduction]: _eris,
                [ScenarioAssumptionsCSVFields.nris_percentage_reduction]: _nris,
                ...exclusionRow
            } = row;
            /* eslint-enable @typescript-eslint/no-unused-vars */
            return exclusionRow as ScenarioAssumptionsCSV;
        }

        return row;
    };

    const handleCsvParsed = (results: ParseResult<ScenarioAssumptionsCSV>) => {
        // The builder lists rows for non-editable projects with the invalid ones.
        const output = readSensitivitiesCsv(results, {
            ...csvRules,
            nonEditableProjectIds: new Set()
        });

        if (!output.ok) {
            segmentTrackSetSensitivities({
                method: "csv",
                numProjectsAffected: 0,
                cluster,
                success: false,
                numInvalidProjects: 0
            });
            SensitivitiesInputToaster.show({
                intent: Intent.DANGER,
                message: output.headerError
            });
            return;
        }

        if (output.invalidProjectIds.length > 0) {
            const debugText = onlyExclusionSupported
                ? "Please check the project ID is valid"
                : "Please check the project ID is valid, and that percentage reductions are a number between 0 and 100";
            SensitivitiesInputToaster.show({
                intent: Intent.WARNING,
                message: (
                    <>
                        Unable to process sensitivities for the following{" "}
                        <b>{output.invalidProjectIds.length}</b> project(s).{" "}
                        {debugText}. Projects:{" "}
                        <b>{output.invalidProjectIds.join(", ")}</b>.
                    </>
                )
            });
        }

        const pqErisNormalized = output.priorQueueErisNormalizedIds;
        if (pqErisNormalized.length > 0) {
            SensitivitiesInputToaster.show({
                intent: Intent.WARNING,
                message: (
                    <>
                        Prior-queue (PQ) projects support full ERIS withdrawal
                        only. ERIS was automatically set to 100% withdrawal for{" "}
                        <b>{pqErisNormalized.length}</b> project(s):{" "}
                        <b>{pqErisNormalized.join(", ")}</b>.
                    </>
                )
            });
        }

        const wholeProjectDropped = output.wholeProjectOnlyDroppedIds;
        if (wholeProjectDropped.length > 0) {
            SensitivitiesInputToaster.show({
                intent: Intent.WARNING,
                message: (
                    <>
                        <b>{wholeProjectDropped.length}</b> project(s) can only
                        be modelled in service (0%) or dropped (100%), so the
                        reduction in your CSV was ignored and they were left in
                        service: <b>{wholeProjectDropped.join(", ")}</b>.
                    </>
                )
            });
        }

        const {
            assumptions,
            withdrawnKeptExcludedIds: keptExcludedProjectIds,
            withdrawnReinstatedIds: reinstatedProjectIds
        } = output;

        if (keptExcludedProjectIds.length > 0) {
            SensitivitiesInputToaster.show({
                intent: Intent.PRIMARY,
                icon: IconNames.INFO_SIGN,
                message: (
                    <>
                        <b>{keptExcludedProjectIds.length}</b> withdrawn
                        project(s) not included in the CSV remained excluded by
                        default. {includeWithdrawnHint}
                    </>
                )
            });
        }

        if (reinstatedProjectIds.length > 0) {
            SensitivitiesInputToaster.show({
                intent: Intent.PRIMARY,
                icon: IconNames.INFO_SIGN,
                message: (
                    <>
                        <b>{reinstatedProjectIds.length}</b> withdrawn
                        project(s) were kept in the scenario because they were
                        included in your CSV:{" "}
                        <b>{reinstatedProjectIds.join(", ")}</b>.
                    </>
                )
            });
        }

        segmentTrackSetSensitivities({
            method: "csv",
            numProjectsAffected: Object.keys(assumptions).length,
            cluster,
            success: true,
            numInvalidProjects: output.skippedRowCount
        });

        setProjectSizeAssumptions({ ...assumptions });
        setIncludedSpeculativeProjectIds([
            ...output.includedSpeculativeProjectIds
        ]);
    };

    const uploadChangeHandler = (e: Event) => {
        if (e.target == null) {
            return;
        }
        const target = e.target as HTMLInputElement;
        if (target.files == null) {
            return;
        }
        const file = target.files[0];
        parseSensitivitiesCsvFile(file).then(handleCsvParsed);
    };

    /**
     * Creates a placeholder file HTML input element and opens it for the user.
     */
    const handleUploadClick = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".csv, text/csv";
        input.onchange = uploadChangeHandler;
        input.click();

        segmentTrackUploadSensitivities({
            region: cluster.region,
            studyCycle: cluster.studyCycle,
            studyPhase: cluster.studyPhase,
            studyGroup: cluster.studyGroup
        });
    };

    const downloadCsvTemplate = () => {
        segmentTrackDownloadSensitivitiesTemplate(cluster);

        const templateName = onlyExclusionSupported
            ? "Withdrawal"
            : "Sensitivities";

        const allAssumptions = {
            ...projectSizeAssumptions
        };

        validProjectIds.forEach((projectId: string) => {
            if (!(projectId in allAssumptions)) {
                allAssumptions[projectId] = {
                    type: ProjectSizeAdjustmentType.PERCENTAGE_REDUCTION,
                    eris: 0,
                    nris: 0
                };
            }
        });

        const templateData = Object.entries(allAssumptions).map(
            ([projectId, assumptions]) => {
                const project = projectById.get(projectId);
                const isSpeculativeProject =
                    speculativeProjectIds.has(projectId);

                if (onlyExclusionSupported) {
                    return buildTemplateRow({ projectId, project });
                }

                if (
                    isSpeculativeProject &&
                    !includedSpeculativeProjectIds.includes(projectId)
                ) {
                    return buildTemplateRow({
                        projectId,
                        project,
                        erisPercentageReduction: 100,
                        nrisPercentageReduction: 100
                    });
                }

                return buildTemplateRow({
                    projectId,
                    project,
                    erisPercentageReduction: assumptions.eris,
                    nrisPercentageReduction: assumptions.nris
                });
            }
        );

        const outputData =
            templateData.length > 0
                ? templateData
                : [buildTemplateRow({ projectId: "" })];

        downloadJSONToCSV({
            json: outputData,
            filename: `Nira - ${templateName} Template.csv`
        });
    };

    const column = (name: string) => (
        <span className={Classes.MONOSPACE_TEXT}>{name}</span>
    );

    return (
        <div className={css["UploadSensitivitiesMenu"]}>
            <Banner
                className={css["UploadSensitivitiesMenu-banner"]}
                intent={Intent.PRIMARY}
                icon={IconNames.INFO_SIGN}
                title={
                    onlyExclusionSupported
                        ? "Only one template column is read on upload"
                        : "Only three template columns are read on upload"
                }
                description={
                    onlyExclusionSupported ? (
                        <>
                            {column(ScenarioAssumptionsCSVFields.project_id)}{" "}
                            lists the projects to withdraw. The other columns
                            are for reference and are not required when
                            uploading.
                        </>
                    ) : (
                        <>
                            {column(ScenarioAssumptionsCSVFields.project_id)},{" "}
                            {column(
                                ScenarioAssumptionsCSVFields.eris_percentage_reduction
                            )}{" "}
                            and{" "}
                            {column(
                                ScenarioAssumptionsCSVFields.nris_percentage_reduction
                            )}{" "}
                            set each project's reduction. The other columns are
                            for reference and are not required when uploading
                            sensitivities.
                        </>
                    )
                }
            />
            <Menu>
                <MenuItem
                    icon={IconNames.TH}
                    text="Download template"
                    label="(.csv)"
                    onClick={downloadCsvTemplate}
                />
                <MenuItem
                    icon={IconNames.UPLOAD}
                    text="Upload sensitivities"
                    label="(.csv)"
                    onClick={handleUploadClick}
                />
            </Menu>
        </div>
    );
};

const SensitivitiesInputToaster = Toaster.create({
    position: Position.TOP
});
